"use client"

import type React from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
} from "@mui/material"

interface PaymentHistory {
  _id: string
  paymentMode: string
  payment: number
  description: string
  receivedBy: string
}

interface ClearPaymentDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: () => void
  item: any
  paymentData: {
    invoiceId: string
    payment_mode: string
    description: string
    payment: number
    due: number
    advance: number
  }
  pendingDues: number
  paymentHistory?: PaymentHistory[]
  onPaymentDataChange: (data: any) => void
  loading: boolean
}

const gradientButtonStyle = {
  minWidth: { xs: "auto", md: "80px" },
  fontSize: { xs: "12px", md: "12px" },
  fontWeight: "600",
  textTransform: "none",
  borderRadius: "8px",
  background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
  color: "#ffffff",
  boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
  transition: "all 0.3s ease-in-out",
  "&:hover": {
    background: "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
    boxShadow: "0 5px 15px rgba(25, 118, 210, .4)",
    transform: "translateY(-1px)",
  },
}

const ClearPaymentDialog: React.FC<ClearPaymentDialogProps> = ({
  open,
  onClose,
  onSubmit,
  paymentData,
  pendingDues,
  paymentHistory,
  onPaymentDataChange,
  loading,
   item,
}) => {
  const totalPaid = paymentHistory?.reduce((sum, history) => sum + history.payment, 0) || 0

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: "600", fontSize: "16px" }}>Clear Payment</DialogTitle>
      <DialogContent>
        {/* Payment History Section */}
        {paymentHistory && paymentHistory.length > 0 && (
          <Box sx={{ mb: 3, mt: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "600", mb: 1, color: "#1976d2", fontSize: "14px" }}>
              Payment History
            </Typography>
            <TableContainer component={Paper} sx={{ maxHeight: 300, boxShadow: 1 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "600", bgcolor: "#f5f5f5", fontSize: "12px" }}>Payment Mode</TableCell>
                    <TableCell align="right" sx={{ fontWeight: "600", bgcolor: "#f5f5f5", fontSize: "12px" }}>
                      Amount
                    </TableCell>
                    <TableCell sx={{ fontWeight: "600", bgcolor: "#f5f5f5", fontSize: "12px" }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: "600", bgcolor: "#f5f5f5", fontSize: "12px" }}>Received By</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentHistory.map((history) => (
                    <TableRow key={history._id} hover>
                      <TableCell sx={{ fontSize: "12px" }}>{history.paymentMode}</TableCell>
                      <TableCell align="right">
                        <Typography
                          sx={{
                            fontWeight: "600",
                            color: history.payment > 0 ? "green" : "inherit",
                            fontSize: "12px",
                          }}
                        >
                          {history.payment.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: "12px" }}>{history.description || "—"}</TableCell>
                      <TableCell sx={{ fontSize: "12px" }}>{history.receivedBy}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: "#f5f5f5", borderTop: "2px solid #1976d2" }}>
                    <TableCell colSpan={4} sx={{ p: 2 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 3 }}>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1 }}>
                          <Typography sx={{ fontSize: "11px", color: "#666", mb: 0.5 }}>Total Amount</Typography>
                          <Typography sx={{ fontSize: "13px", fontWeight: "700", color: "#1565c0" }}>
                            Rs. {item?.grandTotal.toLocaleString()}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1 }}>
                          <Typography sx={{ fontSize: "11px", color: "#666", mb: 0.5 }}>Paid Amount</Typography>
                          <Typography sx={{ fontSize: "13px", fontWeight: "700", color: "#2e7d32" }}>
                            Rs. {totalPaid.toLocaleString()}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1 }}>
                          <Typography sx={{ fontSize: "11px", color: "#666", mb: 0.5 }}>Pending</Typography>
                          <Typography sx={{ fontSize: "13px", fontWeight: "700", color: "#d84315" }}>
                            Rs. {pendingDues.toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <Divider sx={{ my: 2 }} />
          </Box>
        )}

        {pendingDues === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 4,
              px: 2,
              bgcolor: "#e8f5e9",
              borderRadius: "8px",
              border: "1px solid #4caf50",
            }}
          >
            <Typography sx={{ fontSize: "16px", fontWeight: "600", color: "#2e7d32", mb: 1 }}>
              Payment Complete!
            </Typography>
            <Typography sx={{ fontSize: "13px", color: "#666" }}>
              All dues have been cleared. No pending payment required.
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="subtitle1" sx={{ fontWeight: "600", mb: 2, color: "#1976d2", fontSize: "14px" }}>
              New Payment
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Select
                className="custom-input"
                value={paymentData.payment_mode}
                onChange={(e) =>
                  onPaymentDataChange({
                    ...paymentData,
                    payment_mode: e.target.value,
                  })
                }
                fullWidth
                size="small"
                sx={{ fontSize: "13px" }}
              >
                <MenuItem value="Cash" sx={{ fontSize: "13px" }}>
                  Cash
                </MenuItem>
                <MenuItem value="Online" sx={{ fontSize: "13px" }}>
                  Online
                </MenuItem>
              </Select>
              <TextField
                className="custom-input"
                label="Description"
                value={paymentData.description}
                onChange={(e) =>
                  onPaymentDataChange({
                    ...paymentData,
                    description: e.target.value,
                  })
                }
                fullWidth
                multiline
                rows={3}
                size="small"
                placeholder="Enter payment description..."
                InputProps={{ style: { fontSize: "13px" } }}
                InputLabelProps={{ style: { fontSize: "13px" } }}
              />
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <TextField
                  className="custom-input"
                  label="Payment Amount"
                  type="number"
                  value={paymentData.payment === 0 ? "" : paymentData.payment}
                  onChange={(e) => {
                    let newPayment = Number(e.target.value)
                    if (newPayment > pendingDues) {
                      newPayment = pendingDues
                      return
                    }
                    onPaymentDataChange({
                      ...paymentData,
                      payment: newPayment,
                    })
                  }}
                  size="small"
                  sx={{ flex: 1 }}
                  InputProps={{ style: { fontSize: "13px" } }}
                  InputLabelProps={{ style: { fontSize: "13px" } }}
                />
                <TextField
                  className="custom-input"
                  label="Remaining Amount"
                  type="number"
                  value={Math.max(0, pendingDues - paymentData.payment)}
                  size="small"
                  sx={{ flex: 1 }}
                  disabled
                  InputProps={{
                    readOnly: true,
                    style: { fontSize: "13px" },
                  }}
                  InputLabelProps={{ style: { fontSize: "13px" } }}
                />
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ padding: "16px 24px" }}>
        <Button onClick={onClose} variant="outlined" disabled={loading} sx={{ fontSize: "12px" }}>
          Cancel
        </Button>
        {pendingDues !== 0 && (
          <Button
            onClick={onSubmit}
            variant="contained"
            disabled={loading}
            sx={{
              ...gradientButtonStyle,
              minWidth: "100px",
              fontSize: "12px",
            }}
          >
            {loading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Submit"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default ClearPaymentDialog
